import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { type FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import { useGetAssignableBrandsAndBranchesQuery } from "@/api/admin/legalEntity/useLegalEntityHooks";
import {
  BlockWithBorder,
  CustomCardBody,
  CustomLabel,
  Loader,
  Subtitle,
  Switch,
} from "@/components/atoms";
import { InputControl, SelectControl } from "@/components/molecules";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { FormProps as BaseFormProps } from "@/types/common";
import { stabilizePhoneInput } from "@/utils/stabilizePhoneInput";
import { toE164FromString } from "@/utils/toE164";

import { CountryCitySelect } from "./CountryCitySelect";
import { type NewLegalEntityFormType, newLegalEntitySchema } from "./types";

const LegalEntityCreateForm: FC<BaseFormProps<NewLegalEntityFormType>> =
  observer(
    ({ defaultValues, onSubmit, onValidityChange, id, children, classes }) => {
      const form = useForm<NewLegalEntityFormType>({
        resolver: zodResolver(newLegalEntitySchema),
        defaultValues,
        mode: "onChange",
        reValidateMode: "onBlur",
      });

      const { data: optionsData, isPending: isOptionPending } =
        useGetOptionsQuery([
          OptionType.Cities,
          OptionType.Countries,
          OptionType.LegalEntitiesWithoutParent,
          OptionType.Groups,
          OptionType.Brands,
          OptionType.Branches,
        ]);

      const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isValid },
      } = form;

      const linkedGroupId = watch("linkedGroupIds");
      const previousGroupIdRef = useRef<string | null | undefined>(
        linkedGroupId
      );

      const { data: assignableData, isPending: isAssignablePending } =
        useGetAssignableBrandsAndBranchesQuery(linkedGroupId || "");

      // Clear brands and branches when group changes or is cleared
      useEffect(() => {
        // If group changed (not just initial mount)
        if (
          previousGroupIdRef.current !== undefined &&
          previousGroupIdRef.current !== linkedGroupId
        ) {
          setValue("linkedBrandIds", null);
          setValue("linkedBranchIds", null);
        }
        previousGroupIdRef.current = linkedGroupId;
      }, [linkedGroupId, setValue]);

      useEffect(() => {
        onValidityChange?.(isValid);
      }, [isValid, onValidityChange]);

      const handleFormSubmit = (data: NewLegalEntityFormType) => {
        const formattedPhone = toE164FromString(data.phone);
        const payload = {
          ...data,
          phone: formattedPhone,
        };
        onSubmit(payload);
      };

      if (isOptionPending) {
        return <Loader fullscreen />;
      }

      return (
        <Form {...form}>
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            id={id}
            data-testid="group-information-form"
          >
            <CustomCardBody classes={cn("pb-3.5 px-4 md:px-8", classes)}>
              <BlockWithBorder>
                <Subtitle title="Basic information" />
                <div
                  className={cn(
                    "flex align-center gap-x-4 flex-col sm:flex-row"
                  )}
                >
                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="Name"
                    nameField="name"
                    placeholder="Enter official legal entity name"
                    data-testid="new-legal-entity-form-name"
                  />

                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="Commercial Registration (CR) Number"
                    nameField="commercialRegistration"
                    placeholder="Enter CR number"
                    data-testid="new-legal-entity-form-cr-number"
                  />
                </div>

                <div
                  className={cn(
                    "flex align-center gap-x-4 flex-col sm:flex-row"
                  )}
                >
                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="VAT Number"
                    nameField="VAT"
                    placeholder="Enter VAT number"
                    data-testid="new-legal-entity-form-vat"
                  />

                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="IBAN Number"
                    nameField="ibanNumber"
                    placeholder="Enter IBAN number"
                    data-testid="new-legal-entity-form-iban"
                  />
                </div>

                <CountryCitySelect<NewLegalEntityFormType>
                  form={form}
                  countries={optionsData?.countries || []}
                />

                <InputControl<NewLegalEntityFormType>
                  formControl={control}
                  label="Address"
                  nameField="address"
                  placeholder="Enter official legal entity name"
                  data-testid="new-legal-entity-form-address"
                />

                <div
                  className={cn(
                    "flex align-center gap-x-4 flex-col sm:flex-row"
                  )}
                >
                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="Contact email"
                    nameField="email"
                    placeholder="Enter contact email"
                    data-testid="new-legal-entity-form-email"
                  />

                  <InputControl<NewLegalEntityFormType>
                    formControl={control}
                    label="Contact phone"
                    nameField="phone"
                    placeholder="Enter contact phone"
                    data-testid="new-legal-entity-form-phone"
                    onChange={e => {
                      const value = stabilizePhoneInput(e.target.value);
                      setValue("phone", value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>

                <div className="flex items-center gap-2 my-3">
                  <FormField
                    control={control}
                    name="isFundable"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Switch
                            id="isFundable"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-fundable"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <CustomLabel
                    htmlFor="isFundable"
                    label="Fundable legal entity"
                  />
                </div>
              </BlockWithBorder>

              <div className="pt-4">
                <Subtitle title="System linking" />

                <div className="flex flex-col gap-4.5 mb-4">
                  <SelectControl<NewLegalEntityFormType>
                    form={form}
                    nameSelect="parentOfId"
                    label="Parent of"
                    placeholder="None"
                    itemsList={optionsData?.legalEntitiesWithoutParent || []}
                    triggerProps={{
                      "data-testid": "new-legal-entity-form-parent-select",
                    }}
                  />
                  <SelectControl<NewLegalEntityFormType>
                    form={form}
                    nameSelect="linkedGroupIds"
                    label="Linked groups"
                    placeholder="Search and select groups"
                    itemsList={optionsData?.groups || []}
                    triggerProps={{
                      "data-testid": "new-legal-entity-form-linked-groups",
                    }}
                  />
                  <SelectControl<NewLegalEntityFormType>
                    form={form}
                    nameSelect="linkedBrandIds"
                    label="Linked brands"
                    placeholder="Search and select brands"
                    itemsList={
                      assignableData?.brands?.map(brand => ({
                        id: brand.id,
                        name: brand.name,
                      })) || []
                    }
                    disabled={!linkedGroupId || isAssignablePending}
                    triggerProps={{
                      "data-testid": "new-legal-entity-form-linked-brands",
                    }}
                  />
                  <SelectControl<NewLegalEntityFormType>
                    form={form}
                    nameSelect="linkedBranchIds"
                    label="Linked branches"
                    placeholder="Search and select branches"
                    itemsList={
                      assignableData?.branches?.map(branch => ({
                        id: branch.id,
                        name: branch.name,
                      })) || []
                    }
                    disabled={!linkedGroupId || isAssignablePending}
                    triggerProps={{
                      "data-testid": "new-legal-entity-form-linked-branches",
                    }}
                  />
                </div>
              </div>
              {children}
            </CustomCardBody>
          </form>
        </Form>
      );
    }
  );

export default LegalEntityCreateForm;
